import { useEffect, useRef, useState, useMemo } from "react";
import { sleep, getEnvVariables } from "../../../helpers";
import { useForm } from "../../../hooks";
import { ErrorMessage } from "../../ui";
import {
  useScheduleEventStore,
  useScheduleStore,
  usePatientSurgeryStore,
  useFormEventStore,
  useFormMessages,
  useTPatientSurgeryAssessmentRules,
  useSelectOptionsStore,
} from "../../hooks";
import { AssessmentFilterForm } from "../forms";
import { Schedule, ScheduleList } from "../schedules";
import { ConfirmAssessmentModal, CancelAssessmentModal } from "../modals";
import { AssessmentEventsCard } from "../cards";
import { IPS_IDS, PROCEDURE_IDS } from "../../data";

const initialFormDataAssessment = {
  files: [],
};

const formValidationsAssessment = {};

const initialFormDataFilterForm = {
  doctorHeadquarterScheduleId: "",
};

const formValidationsFilterForm = {
  doctorHeadquarterScheduleId: [
    (value) => value.length > 0,
    "Selecciona un doctor",
  ],
};

const configFilterFormByEnv = {
  production: {
    [`${PROCEDURE_IDS.CORE}-${IPS_IDS.SOGAMOSO}`]: {
      doctorHeadquarterScheduleId: "d78816ab-54cd-4407-a63b-00fd25595d12",
    },
  },
  development: {
    [`${PROCEDURE_IDS.CORE}-${IPS_IDS.SOGAMOSO}`]: {
      doctorHeadquarterScheduleId: "63dff4b0-cbdc-48c9-afdf-433d66ffdbd6",
    },
  },
};

const configRender = {
  [`${PROCEDURE_IDS.CORE}-${IPS_IDS.SOGAMOSO}`]: {
    component: ScheduleList,
    props: {
      handleSelectEventRadio: () => {},
      handleAutoSubmitFilterForm: () => {},
      formStateFilterForm: {},
      setIsConfirmAssessmentModal: () => {},
    },
  },
  default: {
    component: Schedule,
    props: {
      handleSelectEvent: () => {},
    },
  },
};

const { nodeEnv } = getEnvVariables();

export const ScheduleAssessmentAccordion = () => {
  // Datos del hook ----------------------------------------//
  const {
    // Props
    patientSurgery,

    // Methods
    startScheduleAssessment,
    startCancelScheduledAssessment,
  } = usePatientSurgeryStore();

  const {
    // Props
    doctors,

    // Methods
    getDoctorsByIpsId,
  } = useSelectOptionsStore();

  const {
    // Props
    scheduleEvents,
    scheduleEvent,

    // Methods
    handleSetScheduleEvent,
    handleClearScheduleEvent,
  } = useScheduleEventStore();

  const {
    // Props
    schedule,

    // Methods
    startClearSchedule,
    getScheduleByDoctorHeadquarterScheduleId,
  } = useScheduleStore();

  const {
    // Props
    isLoading,
    message,
  } = useFormEventStore();

  const {
    // Props
    type,
    uploadFiles,
    showForm,
  } = useTPatientSurgeryAssessmentRules();

  const { procedure, patient } = patientSurgery.data;
  const { user } = patient;
  const { id: procedureParentId } = procedure.procedure;
  const { id: ipsId } = user.ips;

  // States ------------------------------------------------//
  const [formDataAssessment, setFormDataAssessment] = useState(
    initialFormDataAssessment
  );
  const [formDataFilterForm, setFormDataFilterForm] = useState(
    initialFormDataFilterForm
  );
  const [isConfirmAssessmentModal, setIsConfirmAssessmentModal] =
    useState(false);
  const [isCancelAssessmentModal, setIsCancelAssessmentModal] = useState(false);
  const [validateFilterForm, setValidateFilterForm] = useState(false);
  const [eventIndex, setEventIndex] = useState(0);
  const errorRef = useRef(null);
  const scheduleRef = useRef(schedule);

  const matchedConfigFilterForm = useMemo(() => {
    const groupEnv =
      configFilterFormByEnv[nodeEnv] || configFilterFormByEnv.development;
    return groupEnv[`${procedureParentId}-${ipsId}`] || null;
  }, [procedureParentId, ipsId]);

  // Formulario --------------------------------------------//
  const {
    formState: formStateAssessment,
    onFileInputChange: onFileInputChangeAssessment,
    onFileRemove: onFileRemoveAssessment,
    onResetForm: onResetFormAssessment,
    fileError: fileErrorAssessment,
  } = useForm(formDataAssessment, formValidationsAssessment);

  const {
    formState: formStateFilterForm,
    formValidation: formValidationFilterForm,
    onInputChange: onInputChangeFilterForm,
    isFormValid: isFormValidFilterForm,
  } = useForm(formDataFilterForm, formValidationsFilterForm);

  const {
    setFormSubmitted: setFormSubmittedAssessment,
    showErrorMessage: showErrorMessageAssessment,
    showSuccessMessage: showSuccessMessageAssessment,
    messageText: messageTextAssessment,
  } = useFormMessages({
    message,
    onResetForm: onResetFormAssessment,
  });

  const {
    setFormSubmitted: setFormSubmittedFilterForm,
    showErrorMessage: showErrorMessageFilterForm,
    showSuccessMessage: showSuccessMessageFilterForm,
    messageText: messageTextFilterForm,
  } = useFormMessages({
    message,
  });

  // Funciones ---------------------------------------------//
  const handleSelectEvent = (event) => {
    const newEvent = {
      ...event,
      start: event.start.toISOString(),
      end: event.end.toISOString(),
    };

    handleSetScheduleEvent(newEvent);
    setIsConfirmAssessmentModal(true);
  };

  const handleSelectEventRadio = (event) => {
    const newEvent = {
      ...event,
      start: event.start.toISOString(),
      end: event.end.toISOString(),
    };

    handleSetScheduleEvent(newEvent);
  };

  const handleSelectEventToReschedule = (event) => {
    handleSetScheduleEvent(event);
    setIsCancelAssessmentModal(true);
  };

  const handleScheduleSubmit = () => {
    setFormSubmittedAssessment(true);
    startScheduleAssessment(formStateAssessment);
    return;
  };

  const handleScheduleCancel = () => {
    setFormSubmittedAssessment(true);
    startCancelScheduledAssessment();
  };

  const handleScrollToError = async () => {
    await sleep(200);
    errorRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
  };

  const handleSubmitFilterForm = () => {
    setValidateFilterForm(true);
    if (isFormValidFilterForm) {
      setFormSubmittedFilterForm(true);
      getScheduleByDoctorHeadquarterScheduleId(formStateFilterForm);
    }
  };

  const handleAutoSubmitFilterForm = ({ formState }) => {
    setFormSubmittedFilterForm(true);
    getScheduleByDoctorHeadquarterScheduleId(formState, { eventIndex });
    setEventIndex((prev) => prev + 1);
  };

  // Efectos -----------------------------------------------//
  useEffect(() => {
    if (
      !isConfirmAssessmentModal &&
      !isCancelAssessmentModal &&
      scheduleEvent &&
      (showSuccessMessageAssessment || showErrorMessageAssessment)
    ) {
      handleClearScheduleEvent();
      onResetFormAssessment();
    }
  }, [
    isConfirmAssessmentModal,
    scheduleEvent,
    isCancelAssessmentModal,
    showSuccessMessageAssessment,
    showErrorMessageAssessment,
  ]);

  useEffect(() => {
    setFormDataAssessment(() => {
      const updated = {
        ...formStateAssessment,
        type,
      };

      return updated;
    });
  }, [type]);

  useEffect(() => {
    if (showErrorMessageAssessment) {
      if (isConfirmAssessmentModal) {
        setIsConfirmAssessmentModal(false);
      }
      if (isCancelAssessmentModal) {
        setIsCancelAssessmentModal(false);
      }

      if (errorRef.current) {
        handleScrollToError();
      }
    }
  }, [showErrorMessageAssessment]);

  useEffect(() => {
    if (showSuccessMessageAssessment && isConfirmAssessmentModal) {
      setIsConfirmAssessmentModal(false);
    }
    if (showSuccessMessageAssessment && isCancelAssessmentModal) {
      setIsCancelAssessmentModal(false);
    }
  }, [showSuccessMessageAssessment]);

  useEffect(() => {
    if (showSuccessMessageFilterForm) {
      setFormSubmittedFilterForm(false);
    }
  }, [showSuccessMessageFilterForm]);

  useEffect(() => {
    if (doctors.data.length === 0 && doctors.message === null) {
      getDoctorsByIpsId({ ipsId });
    }
  }, [doctors.data, getDoctorsByIpsId, ipsId]);

  useEffect(() => {
    if (matchedConfigFilterForm && doctors.data.length > 0) {
      const { doctorHeadquarterScheduleId } = matchedConfigFilterForm;

      const newFormState = {
        ...formStateFilterForm,
        doctorHeadquarterScheduleId,
      };

      setFormDataFilterForm(newFormState);
      handleAutoSubmitFilterForm({ formState: newFormState });
    }
  }, [matchedConfigFilterForm, doctors.data.length]);

  useEffect(() => {
    scheduleRef.current = schedule;
  }, [schedule]);

  useEffect(() => {
    return () => {
      if (!scheduleRef.current) return;
      startClearSchedule();
    };
  }, []);

  const propsMapping = {
    handleSelectEvent,
    handleSelectEventRadio,
    handleAutoSubmitFilterForm,
    setIsConfirmAssessmentModal,
    formStateFilterForm,
    eventIndex,
  };

  const propsToModalAssessment = {
    formState: formStateAssessment,
    onFileInputChange: onFileInputChangeAssessment,
    onFileRemove: onFileRemoveAssessment,
    fileError: fileErrorAssessment,
    uploadFiles,
    isLoading,
  };

  const propsToFilterForm = {
    // Props
    formState: formStateFilterForm,
    formValidation: formValidationFilterForm,
    showErrorMessage: showErrorMessageFilterForm,
    messageText: messageTextFilterForm,
    validateForm: validateFilterForm,
    matchedConfig: matchedConfigFilterForm,

    // Methods
    onInputChange: onInputChangeFilterForm,
    handleSubmitFilterForm,
    handleAutoSubmitFilterForm,
  };

  const matchedConfig = useMemo(() => {
    const configMatch =
      configRender[`${procedureParentId}-${ipsId}`] || configRender.default;
    const propsWithValues = {};

    Object.keys(configMatch.props).forEach((propKey) => {
      propsWithValues[propKey] = propsMapping[propKey];
    });

    return {
      component: configMatch.component,
      props: propsWithValues,
    };
  }, [procedureParentId, ipsId, formStateFilterForm, eventIndex]);

  // useEffect(() => {
  //   console.log("formStateFilterForm", formStateFilterForm);
  // }, [formStateFilterForm]);

  return (
    <>
      <div className="accordion" id="scheduleAssessmentAccordion">
        <div className="accordion-item">
          <h2 className="accordion-header">
            <button
              className={`accordion-button`}
              type="button"
              data-bs-toggle="collapse"
              data-bs-target={`#scheduleAssessment`}
              aria-expanded="true"
              aria-controls={`scheduleAssessment`}
            >
              Procedimiento: {procedure.procedure.title || "Sin título"}
            </button>
          </h2>
          <div
            id={`scheduleAssessment`}
            className={`accordion-collapse collapse show`}
            data-bs-parent="#scheduleAssessmentAccordion"
          >
            <div className="accordion-body">
              <div className="vstack gap-4">
                {showErrorMessageAssessment && (
                  <ErrorMessage ref={errorRef}>
                    {messageTextAssessment}
                  </ErrorMessage>
                )}

                {scheduleEvents.length > 0 && (
                  <AssessmentEventsCard
                    handleSelectEventToReschedule={
                      handleSelectEventToReschedule
                    }
                  />
                )}

                {showForm && (
                  <>
                    <AssessmentFilterForm propsForm={propsToFilterForm} />
                    {schedule && (
                      <matchedConfig.component {...matchedConfig.props} />
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <ConfirmAssessmentModal
        isOpen={isConfirmAssessmentModal}
        setIsOpen={setIsConfirmAssessmentModal}
        onSubmit={handleScheduleSubmit}
        propsForm={propsToModalAssessment}
      />

      <CancelAssessmentModal
        isOpen={isCancelAssessmentModal}
        setIsOpen={setIsCancelAssessmentModal}
        onSubmit={handleScheduleCancel}
        propsForm={propsToModalAssessment}
      />
    </>
  );
};
